/**
 * MediaPipe Landmark Stabilization
 * Implements One-Euro Filter, EMA, and outlier rejection for smooth pose tracking
 */

import type { OverlayConfig } from '../../config/poseOverlay.config';

// Landmark format: [x, y, z, visibility?]
export type Landmark = [number, number, number, number?];
export type LandmarkSet = Landmark[];

// Smoothing state for a single landmark
interface SmoothingState {
  // One-Euro filter state
  oneEuro?: {
    x: OneEuroState;
    y: OneEuroState;
    z: OneEuroState;
  };
  
  // EMA filter state
  ema?: {
    x: number;
    y: number; 
    z: number;
    alpha: number;
  };
  
  // Last known good position for outlier detection
  lastGood?: Landmark;
  lastTimestamp?: number;
}

interface OneEuroState {
  value: number;
  derivative: number;
  lastValue: number;
  lastDerivative: number;
  lastTimestamp: number;
}

// Global smoothing state (keyed by landmark index)
const smoothingStates = new Map<number, SmoothingState>();

/**
 * One-Euro Filter implementation
 * Reduces jitter while maintaining responsiveness to quick movements
 */
class OneEuroFilter {
  private minCutoff: number;
  private beta: number;
  private dCutoff: number;
  
  constructor(minCutoff = 1.0, beta = 0.007, dCutoff = 1.0) {
    this.minCutoff = minCutoff;
    this.beta = beta;
    this.dCutoff = dCutoff;
  }
  
  // Low-pass filter with dynamic cutoff
  private lowPassFilter(value: number, lastValue: number, cutoff: number, dt: number): number {
    const tau = 1.0 / (2 * Math.PI * cutoff);
    const alpha = dt / (dt + tau);
    return alpha * value + (1 - alpha) * lastValue;
  }
  
  // Apply One-Euro filter to single dimension
  filter(value: number, state: OneEuroState, timestamp: number): number {
    const dt = timestamp - state.lastTimestamp;
    
    if (dt <= 0) return state.value; // No time passed
    
    // Calculate derivative (rate of change)
    const derivative = (value - state.lastValue) / dt;
    
    // Smooth the derivative
    const smoothedDerivative = this.lowPassFilter(
      derivative, 
      state.lastDerivative, 
      this.dCutoff, 
      dt
    );
    
    // Calculate dynamic cutoff based on speed of movement
    const cutoff = this.minCutoff + this.beta * Math.abs(smoothedDerivative);
    
    // Apply main filter with dynamic cutoff
    const smoothedValue = this.lowPassFilter(value, state.lastValue, cutoff, dt);
    
    // Update state
    state.lastValue = smoothedValue;
    state.lastDerivative = smoothedDerivative;
    state.lastTimestamp = timestamp;
    state.value = smoothedValue;
    state.derivative = smoothedDerivative;
    
    return smoothedValue;
  }
}

/**
 * Initialize One-Euro filter state for a landmark
 */
function initOneEuroState(value: number, timestamp: number): OneEuroState {
  return {
    value,
    derivative: 0,
    lastValue: value,
    lastDerivative: 0,
    lastTimestamp: timestamp
  };
}

/**
 * Apply visibility-based weighting to landmarks
 * Low visibility landmarks get reduced influence in smoothing
 */
function applyVisibilityWeights(landmarks: LandmarkSet): LandmarkSet {
  return landmarks.map(landmark => {
    if (!landmark || landmark.length < 4) return landmark;
    
    const [x, y, z, visibility = 1.0] = landmark;
    
    // Reduce influence of low-visibility landmarks
    const weight = Math.max(0.1, visibility); // Minimum 10% influence
    
    return [x, y, z, weight] as Landmark;
  });
}

/**
 * Detect and reject outliers using statistical methods
 */
function rejectOutliers(
  current: LandmarkSet, 
  state: Map<number, SmoothingState>, 
  config: OverlayConfig['outlier']
): LandmarkSet {
  return current.map((landmark, index) => {
    if (!landmark) return landmark;
    
    const landmarkState = state.get(index);
    if (!landmarkState?.lastGood) {
      // First frame, accept as-is
      if (!state.has(index)) {
        state.set(index, {});
      }
      state.get(index)!.lastGood = landmark;
      return landmark;
    }
    
    const [x, y, z, visibility = 1.0] = landmark;
    const [lastX, lastY, lastZ] = landmarkState.lastGood;
    
    // Calculate pixel distance from last good position
    const distance = Math.sqrt(
      Math.pow(x - lastX, 2) + 
      Math.pow(y - lastY, 2)
    );
    
    // Check if movement is too large (outlier)
    if (distance > config.maxJumpPx && visibility > 0.5) {
      console.warn(`[Stabilize] Outlier rejected at landmark ${index}: distance=${distance.toFixed(1)}px`);
      // Return interpolated position (could be more sophisticated)
      const alpha = 0.3; // Partial movement toward outlier
      return [
        lastX + alpha * (x - lastX),
        lastY + alpha * (y - lastY), 
        lastZ + alpha * (z - lastZ),
        visibility * 0.5 // Reduce confidence
      ] as Landmark;
    }
    
    // Accept landmark and update last good position
    landmarkState.lastGood = landmark;
    return landmark;
  });
}

/**
 * Apply EMA (Exponential Moving Average) smoothing
 */
function applyEMASmoothing(
  current: LandmarkSet,
  state: Map<number, SmoothingState>,
  config: OverlayConfig['ema']
): LandmarkSet {
  return current.map((landmark, index) => {
    if (!landmark) return landmark;
    
    const [x, y, z, visibility = 1.0] = landmark;
    
    if (!state.has(index)) {
      state.set(index, {
        ema: { x, y, z, alpha: config.alpha }
      });
      return landmark;
    }
    
    const landmarkState = state.get(index)!;
    if (!landmarkState.ema) {
      landmarkState.ema = { x, y, z, alpha: config.alpha };
      return landmark;
    }
    
    // Weight smoothing by visibility
    const alpha = config.alpha * visibility;
    
    landmarkState.ema.x = alpha * x + (1 - alpha) * landmarkState.ema.x;
    landmarkState.ema.y = alpha * y + (1 - alpha) * landmarkState.ema.y;
    landmarkState.ema.z = alpha * z + (1 - alpha) * landmarkState.ema.z;
    
    return [
      landmarkState.ema.x,
      landmarkState.ema.y,
      landmarkState.ema.z,
      visibility
    ] as Landmark;
  });
}

/**
 * Enhanced outlier rejection with velocity checking
 */
function rejectOutliersAdvanced(
  current: LandmarkSet, 
  state: Map<number, SmoothingState>, 
  config: OverlayConfig['outlier'],
  phase?: string
): LandmarkSet {
  return current.map((landmark, index) => {
    if (!landmark) return landmark;
    
    const landmarkState = state.get(index);
    if (!landmarkState?.lastGood) {
      // First frame, accept as-is
      if (!state.has(index)) {
        state.set(index, {});
      }
      state.get(index)!.lastGood = landmark;
      return landmark;
    }
    
    const [x, y, z, visibility = 1.0] = landmark;
    const [lastX, lastY, lastZ] = landmarkState.lastGood;
    
    // Calculate pixel distance from last good position
    const distance = Math.sqrt(
      Math.pow(x - lastX, 2) + 
      Math.pow(y - lastY, 2)
    );
    
    // Enhanced outlier detection with phase awareness
    let maxJump = config.maxJumpPx;
    if (phase === 'warmup') {
      maxJump *= 1.5; // More lenient during warmup
    } else if (phase === 'still') {
      maxJump *= 0.7; // More strict during still phases
    }
    
    // Check if movement is too large (outlier)
    if (distance > maxJump && visibility > 0.5) {
      console.warn(`[Stabilize] Enhanced outlier rejected at landmark ${index}: distance=${distance.toFixed(1)}px (max=${maxJump.toFixed(1)}px, phase=${phase})`);
      
      // Improved interpolation based on visibility
      const alpha = Math.min(0.4, visibility * 0.5); // More conservative interpolation
      return [
        lastX + alpha * (x - lastX),
        lastY + alpha * (y - lastY), 
        lastZ + alpha * (z - lastZ),
        visibility * 0.6 // Reduce confidence more aggressively
      ] as Landmark;
    }
    
    // Accept landmark and update last good position
    landmarkState.lastGood = landmark;
    return landmark;
  });
}

/**
 * Apply One-Euro Filter smoothing
 */
function applyOneEuroSmoothing(
  current: LandmarkSet,
  state: Map<number, SmoothingState>, 
  config: OverlayConfig['oneEuro'],
  timestamp: number
): LandmarkSet {
  const filter = new OneEuroFilter(config.minCutoff, config.beta, config.dCutoff);
  
  return current.map((landmark, index) => {
    if (!landmark) return landmark;
    
    const [x, y, z, visibility = 1.0] = landmark;
    
    if (!state.has(index)) {
      state.set(index, {
        oneEuro: {
          x: initOneEuroState(x, timestamp),
          y: initOneEuroState(y, timestamp), 
          z: initOneEuroState(z, timestamp)
        }
      });
      return landmark;
    }
    
    const landmarkState = state.get(index)!;
    if (!landmarkState.oneEuro) {
      landmarkState.oneEuro = {
        x: initOneEuroState(x, timestamp),
        y: initOneEuroState(y, timestamp),
        z: initOneEuroState(z, timestamp)
      };
      return landmark;
    }
    
    // Apply filtering with visibility weighting
    const weight = Math.max(0.1, visibility);
    const weightedX = x * weight + landmarkState.oneEuro.x.value * (1 - weight);
    const weightedY = y * weight + landmarkState.oneEuro.y.value * (1 - weight);
    const weightedZ = z * weight + landmarkState.oneEuro.z.value * (1 - weight);
    
    const smoothedX = filter.filter(weightedX, landmarkState.oneEuro.x, timestamp);
    const smoothedY = filter.filter(weightedY, landmarkState.oneEuro.y, timestamp);
    const smoothedZ = filter.filter(weightedZ, landmarkState.oneEuro.z, timestamp);
    
    return [smoothedX, smoothedY, smoothedZ, visibility] as Landmark;
  });
}

/**
 * Apply One-Euro Filter smoothing with per-joint multipliers and dynamic params
 */
function applyOneEuroSmoothingAdvanced(
  current: LandmarkSet,
  state: Map<number, SmoothingState>, 
  baseParams: { minCutoff: number; beta: number; dCutoff: number },
  timestamp: number,
  perJointMultipliers?: { wristMult?: number; ankleMult?: number }
): LandmarkSet {
  return current.map((landmark, index) => {
    if (!landmark) return landmark;
    
    const [x, y, z, visibility = 1.0] = landmark;
    
    // Apply per-joint parameter adjustments
    const params = { ...baseParams };
    if (perJointMultipliers) {
      // Wrist joints (15, 16) get extra smoothing
      if (index === 15 || index === 16) {
        params.minCutoff *= perJointMultipliers.wristMult || 1.0;
        params.beta *= perJointMultipliers.wristMult || 1.0;
      }
      // Ankle joints (27, 28) get extra smoothing  
      if (index === 27 || index === 28) {
        params.minCutoff *= perJointMultipliers.ankleMult || 1.0;
        params.beta *= perJointMultipliers.ankleMult || 1.0;
      }
    }
    
    const filter = new OneEuroFilter(params.minCutoff, params.beta, params.dCutoff);
    
    if (!state.has(index)) {
      state.set(index, {
        oneEuro: {
          x: initOneEuroState(x, timestamp),
          y: initOneEuroState(y, timestamp), 
          z: initOneEuroState(z, timestamp)
        }
      });
      return landmark;
    }
    
    const landmarkState = state.get(index)!;
    if (!landmarkState.oneEuro) {
      landmarkState.oneEuro = {
        x: initOneEuroState(x, timestamp),
        y: initOneEuroState(y, timestamp),
        z: initOneEuroState(z, timestamp)
      };
      return landmark;
    }
    
    // Apply filtering with visibility weighting
    const weight = Math.max(0.1, visibility);
    const weightedX = x * weight + landmarkState.oneEuro.x.value * (1 - weight);
    const weightedY = y * weight + landmarkState.oneEuro.y.value * (1 - weight);
    const weightedZ = z * weight + landmarkState.oneEuro.z.value * (1 - weight);
    
    const smoothedX = filter.filter(weightedX, landmarkState.oneEuro.x, timestamp);
    const smoothedY = filter.filter(weightedY, landmarkState.oneEuro.y, timestamp);
    const smoothedZ = filter.filter(weightedZ, landmarkState.oneEuro.z, timestamp);
    
    return [smoothedX, smoothedY, smoothedZ, visibility] as Landmark;
  });
}

/**
 * Enhanced smoothing function with dynamic parameters
 */
export function smoothLandmarksAdvanced(
  rawLandmarks: LandmarkSet,
  config: OverlayConfig,
  timestamp: number = performance.now(),
  dynamicParams?: {
    minCutoff?: number;
    beta?: number;
    dCutoff?: number;
    phase?: 'warmup' | 'still' | 'motion' | 'endcap';
    perJointMultipliers?: { wristMult?: number; ankleMult?: number };
  }
): LandmarkSet {
  if (!rawLandmarks || rawLandmarks.length === 0) {
    return rawLandmarks;
  }
  
  // Step 1: Apply visibility weights
  let landmarks = applyVisibilityWeights(rawLandmarks);
  
  // Step 2: Reject outliers with enhanced velocity checking
  landmarks = rejectOutliersAdvanced(landmarks, smoothingStates, config.outlier, dynamicParams?.phase);
  
  // Step 3: Apply main smoothing filter with dynamic parameters
  if (config.filter === 'oneEuro') {
    const params = dynamicParams ? {
      minCutoff: dynamicParams.minCutoff ?? config.oneEuro.minCutoff,
      beta: dynamicParams.beta ?? config.oneEuro.beta,
      dCutoff: dynamicParams.dCutoff ?? config.oneEuro.dCutoff
    } : config.oneEuro;
    
    landmarks = applyOneEuroSmoothingAdvanced(
      landmarks, 
      smoothingStates, 
      params, 
      timestamp,
      dynamicParams?.perJointMultipliers
    );
  } else {
    landmarks = applyEMASmoothing(landmarks, smoothingStates, config.ema);
  }
  
  return landmarks;
}

/**
 * Legacy smoothing function (backward compatibility)
 */
export function smoothLandmarks(
  rawLandmarks: LandmarkSet,
  config: OverlayConfig,
  timestamp: number = performance.now()
): LandmarkSet {
  return smoothLandmarksAdvanced(rawLandmarks, config, timestamp);
}

/**
 * Reset smoothing state (call when switching videos or major scene changes)
 */
export function resetSmoothingState(): void {
  smoothingStates.clear();
  console.log('[Stabilize] Smoothing state reset');
}

/**
 * Get current smoothing statistics for debugging
 */
export function getSmoothingStats() {
  return {
    activeLandmarks: smoothingStates.size,
    filterType: 'oneEuro', // Could be dynamic based on last used config
    stateKeys: Array.from(smoothingStates.keys())
  };
}
